import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Box, Typography } from "@mui/material";
import PrologueImage from "../components/PrologueImage";
import BodyCard from "../components/BodyCard";
import Section from "../components/Section";
import StaffSection from "../components/StaffSection";
import CustomCircularProgress from "../components/CustomCircularProgress";
import { StaffCategory } from "../DTOs/responses/staff";
import { useStaff } from "../hooks/useStaff";

// Presentation per category. The order the sections render in comes from the
// backend, which returns a group for every category.
const categoryLabels: Record<
  StaffCategory,
  { titleKey: string; roleKey: string; featured?: boolean }
> = {
  professor: {
    titleKey: "peopleProfessor",
    roleKey: "peopleProfessorRole",
    featured: true,
  },
  researcher: {
    titleKey: "peopleResearchers",
    roleKey: "peopleResearcherRole",
  },
  phd: { titleKey: "peoplePhd", roleKey: "peoplePhdRole" },
  msc: { titleKey: "peopleMsc", roleKey: "peopleMscRole" },
};

export default function People() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useStaff();

  const content = () => {
    if (isLoading) return <CustomCircularProgress color="#C46A2D" />;
    if (isError || !data)
      return (
        <Typography sx={{ color: "#62605A", textAlign: "center" }}>
          {t("peopleError")}
        </Typography>
      );

    return (
      <Box>
        {data.groups.map((group) => {
          // A category the backend knows about but this page does not has no
          // heading to render under, so it is skipped rather than crashing.
          const labels = categoryLabels[group.id];
          if (!labels) return null;

          return (
            <StaffSection
              key={group.id}
              members={group.members}
              titleKey={labels.titleKey}
              roleKey={labels.roleKey}
              featured={labels.featured}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Helmet>
        <title>{`${t("people")} | PEL Group`}</title>
        <meta name="description" content={t("peopleIntro")} />
      </Helmet>
      <PrologueImage
        title={t("people")}
        subtitle={t("peopleIntro")}
        image="/home-carousel-5.jpg"
      />
      <BodyCard>
        <Section content={content()} />
      </BodyCard>
    </>
  );
}
